import React, { useEffect, useState } from "react";
import {
  Box, Typography, Button, Paper, Divider, CircularProgress, TextField,
  Snackbar, useTheme, Rating
} from "@mui/material";
import { useTranslation } from "react-i18next";
import api from "../services/api";
import ReviewsStars from "./ReviewsStars";
import AppReviewModal from "./AppReviewModal";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import InstagramIcon from "@mui/icons-material/Instagram";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useAuth } from "../context/AuthContext"; // <-- μην το ξεχάσεις
import { FRONTEND_URL } from "../config/urls";

export default function ReviewsPage() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { isLoggedIn, user } = useAuth();

  // Review state
  const [appReviews, setAppReviews] = useState([]);
  const [showAppReviewModal, setShowAppReviewModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copySnackbarOpen, setCopySnackbarOpen] = useState(false);
  const [reviewName, setReviewName] = useState("");
  const [newAppReview, setNewAppReview] = useState("");
  const [newRating, setNewRating] = useState(5);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Share URL
  const shareUrl = `${FRONTEND_URL}/${i18n.language}`;

  useEffect(() => {
    api.appReviews.list()
      .then(res => setAppReviews(Array.isArray(res.data) ? res.data : []))
      .finally(() => setLoading(false));
  }, []);

  // --- Handlers ---
  const shareOnFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`,
      "_blank"
    );
  };
  const shareOnTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(t("share_app_tweet") || "Δείτε το inCooking!")}`,
      "_blank"
    );
  };

  // Υποβολή νέας κριτικής (inline)
  const handleAddAppReview = async () => {
    if (!newAppReview.trim() || (!isLoggedIn && !reviewName.trim())) return;
    try {
      const payload = {
        text: newAppReview,
        rating: newRating,
        username: isLoggedIn ? (user?.username || undefined) : reviewName,
      };
      await api.appReviews.add(payload);
      setAppReviews([
        ...appReviews,
        {
          user: isLoggedIn ? (user?.username || "Εσύ") : reviewName,
          username: isLoggedIn ? (user?.username || "Εσύ") : reviewName,
          text: newAppReview,
          rating: newRating
        }
      ]);
      setNewAppReview("");
      setReviewName("");
      setNewRating(5);
      setSnackbarOpen(true);
    } catch (e) {
      setSnackbarOpen(false);
      alert("Υπήρξε σφάλμα στην υποβολή της αξιολόγησης.");
    }
  };

  // --- Render ---
  return (
    <Box sx={{ maxWidth: 720, mx: "auto", mt: 6, p: 3, textAlign: "center" }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2, color: "primary.main" }}>
        {t("user_reviews_title", "Κριτικές Χρηστών & Αξιολογήσεις")}
      </Typography>
      <Typography variant="body1" sx={{ mb: 2, color: "text.secondary" }}>
        {t("user_reviews_intro", "Διαβάστε εμπειρίες, αφήστε τη δική σας αξιολόγηση και μοιραστείτε το inCooking!")}
      </Typography>
      <Divider sx={{ my: 3 }} />
      <ReviewsStars />

      <Button
        variant="contained"
        color="primary"
        onClick={() => setShowAppReviewModal(true)}
        sx={{ my: 2, fontWeight: 700 }}
      >
        {t("rate_the_app")}
      </Button>

      <AppReviewModal
        open={showAppReviewModal}
        onClose={() => setShowAppReviewModal(false)}
        onSubmitted={() => {
          api.appReviews.list()
            .then(res => setAppReviews(Array.isArray(res.data) ? res.data : []))
            .catch(() => setAppReviews([]));
        }}
      />

      {loading ? (
        <CircularProgress sx={{ mt: 5 }} />
      ) : (
        <Box sx={{ mt: 3 }}>
          {(!Array.isArray(appReviews) || appReviews.length === 0) ? (
            <Typography color="text.secondary" sx={{ mt: 4 }}>
              {t("no_app_reviews_yet") || "Δεν υπάρχουν αξιολογήσεις ακόμα."}
            </Typography>
          ) : (
            appReviews
              .filter(r => r.text && r.text.trim() !== "")
              .map((r, idx) => (
                <Paper
                  key={idx}
                  elevation={3}
                  sx={{
                    p: 3, mb: 2, borderRadius: 4, boxShadow: 4,
                    background: theme.palette.mode === "dark"
                      ? "#232936" : "#fffdfa",
                    position: "relative",
                  }}
                >
                  {/* Αστέρια */}
                  <Rating value={r.rating || 5} readOnly size="small" sx={{ mb: 1 }} />
                  {/* Όνομα χρήστη */}
                  <Typography fontWeight={700} sx={{ color: "#1976d2", mb: 1 }}>
                    {r.username || r.user || "Χρήστης"}
                  </Typography>
                  {/* Κείμενο */}
                  <Typography sx={{ fontStyle: "italic", fontSize: "1.09rem", color: "text.primary" }}>
                    {r.text}
                  </Typography>
                </Paper>
              ))
          )}

          {/* Premium Pro Review Form */}
          <Box sx={{
            my: 5,
            p: 3,
            bgcolor: theme.palette.mode === "dark" ? "#222e3a" : "#f6fbff",
            borderRadius: 4,
            boxShadow: "0 2px 16px #aee2ff22",
            maxWidth: 520,
            mx: "auto",
            textAlign: "left"
          }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
              {t("leave_a_review", "Άφησε τη δική σου αξιολόγηση")}
            </Typography>
            {/* Επιλογή Αστεριών */}
            <Box sx={{ display: "flex", alignItems: "center", mb: 2, mt: 0.5 }}>
              <Typography sx={{ mr: 1 }}>{t("your_rating", "Βαθμολογία")}:</Typography>
              <Rating
                value={newRating}
                onChange={(_, newValue) => setNewRating(newValue)}
                size="large"
              />
            </Box>
            {!isLoggedIn && (
              <TextField
                label={t("your_name", "Το όνομά σου")}
                value={reviewName}
                onChange={e => setReviewName(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
            )}
            <TextField
              label={t("leave_app_review", "Γράψε τα σχόλιά σου")}
              value={newAppReview}
              onChange={e => setNewAppReview(e.target.value)}
              multiline
              rows={3}
              fullWidth
              sx={{ mb: 2 }}
            />
            <Button
              variant="contained"
              onClick={handleAddAppReview}
              disabled={
                !newAppReview.trim() ||
                (!isLoggedIn && !reviewName.trim())
              }
              sx={{ fontWeight: 700, px: 4, py: 1.4, borderRadius: 3, fontSize: "1.06rem" }}
            >
              {t("submit_review", "Υποβολή αξιολόγησης")}
            </Button>
          </Box>
        </Box>
      )}

      {/* Κουμπιά κοινοποίησης */}
      <Box sx={{ mt: 6, mb: 3, display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<FacebookIcon sx={{ color: "#fff" }} fontSize="medium" />}
          onClick={shareOnFacebook}
          fullWidth={isMobile}
        >
          {t("share_on_facebook")}
        </Button>
        <Button
          variant="contained"
          color="info"
          startIcon={<TwitterIcon sx={{ color: "#fff" }} fontSize="medium" />}
          onClick={shareOnTwitter}
          fullWidth={isMobile}
        >
          {t("share_on_twitter")}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<InstagramIcon />}
          onClick={() => {
            navigator.clipboard.writeText(shareUrl);
            setCopySnackbarOpen(true);
          }}
          fullWidth={isMobile}
        >
          {t("copy_link_for_instagram")}
        </Button>
      </Box>

      {/* Snackbar επιτυχούς αξιολόγησης */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        message={t("review_success_message", "Ευχαριστούμε για την αξιολόγησή σας!")}
      />

      {/* Snackbar για Instagram link */}
      <Snackbar
        open={copySnackbarOpen}
        autoHideDuration={2500}
        onClose={() => setCopySnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        message={t("link_copied_instagram")}
      />
    </Box>
  );
}
